import React from 'react';

import CardDialog from '~/components/CardDialog';
import Account from '~/components/SystemConfiguration/CliveConfiguration/Account';
import CliveConfigurationTab from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationTab';
import ConfigurationScreenWithTabs from '~/components/SystemConfiguration/ConfigurationScreenWithTabs';
import OperationsBreadcrumbs from '~/components/SystemConfiguration/OperationsBreadcrumbs';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import { ADMIN_ROUTES } from '~/routesConstants';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import AgentConfigurationTab from './AgentConfigurationTab';
import CliveFeatureFlagsTab from './CliveFeatureFlagsTab';
import ExternalToolApisTab from './ExternalToolApisTab';
import PredefinedPromptsTab from './PredefinedPromptsTab';
import QaConfigurationTab from './QaConfiguration';

interface CliveAccountConfig {
  account_id?: string;
}

const CONFIGURATION_SUBTITLE = 'Set up clive configuration.';

const CliveConfiguration: React.FC = () => {
  const { organization: sysconfigOrg } = useSysconfig() as { organization: OrganizationModel };
  const baseConfigsRoute = `/api/v1/organizations/${sysconfigOrg?.id}/ai_configuration`;

  const {
    isLoading,
    isError,
    data: account,
    reloadData: reloadAccount,
  } = useDataFetcher<CliveAccountConfig>(baseConfigsRoute);

  const isInternalAdmin = Boolean(sysconfigOrg);
  const isSubOrgsEnabled = sysconfigOrg.sub_organizations_enabled;
  const hasAccount = Boolean(account?.account_id);
  const isQaConfigurationEnabled = isFeatureEnabled(sysconfigOrg, CONFIGURATION_FEATURES_NAMES.AI_EXPERTS_QA);
  const isExternalToolApisEnabled = isFeatureEnabled(sysconfigOrg, CONFIGURATION_FEATURES_NAMES.EXTERNAL_TOOL_APIS);
  const isAgentConfigurationEnabled = isFeatureEnabled(sysconfigOrg, CONFIGURATION_FEATURES_NAMES.AGENT_CONFIGURATION);

  const tabs = [
    {
      label: 'Account',
      url: ADMIN_ROUTES.CLIVE_CONFIGURATION.ACCOUNT,
      component: () => (
        <Account account={account} isLoading={isLoading} isError={isError} reloadAccount={reloadAccount} />
      ),
      isEnabled: true,
    },
    {
      label: isSubOrgsEnabled ? 'Sub Organizations Configuration' : 'Organization Configuration',
      url: ADMIN_ROUTES.CLIVE_CONFIGURATION.CONFIGURATION,
      component: CliveConfigurationTab,
      isEnabled: hasAccount,
    },
    {
      label: 'Features Management',
      url: ADMIN_ROUTES.CLIVE_CONFIGURATION.FEATURES,
      component: CliveFeatureFlagsTab,
      isEnabled: isInternalAdmin && hasAccount,
    },
    {
      label: 'Agent Configuration',
      url: ADMIN_ROUTES.CLIVE_CONFIGURATION.AGENT_CONFIGURATION,
      component: AgentConfigurationTab,
      isEnabled: hasAccount && isAgentConfigurationEnabled,
    },
    {
      label: 'Chat Suggestions',
      url: ADMIN_ROUTES.CLIVE_CONFIGURATION.PREDEFINED_PROMPTS,
      component: PredefinedPromptsTab,
      isEnabled: hasAccount,
    },
    {
      label: 'QA Configuration',
      url: ADMIN_ROUTES.CLIVE_CONFIGURATION.QA_CONFIGURATION,
      component: QaConfigurationTab,
      // Neither of the tab's paths needs a prior enablement gesture: the load
      // (GET .../ai_experts/scopes) answers an empty list for an organization that has
      // configured nothing, and the save upserts the scope's row, creating it when absent.
      isEnabled: hasAccount && isQaConfigurationEnabled,
    },
    {
      label: 'External Tool APIs',
      url: ADMIN_ROUTES.CLIVE_CONFIGURATION.EXTERNAL_TOOL_APIS,
      component: ExternalToolApisTab,
      isEnabled: hasAccount && isExternalToolApisEnabled,
    },
  ];

  return (
    <CardDialog noCardTitle>
      <OperationsBreadcrumbs currentTab="Clive Configuration" />
      <ConfigurationScreenWithTabs
        tabs={tabs}
        title="Clive CONFIGURATION"
        subtitle={CONFIGURATION_SUBTITLE}
        operationTabName="Clive Configuration"
      />
    </CardDialog>
  );
};

export default CliveConfiguration;
